import React, { useEffect, useMemo, useRef, useState } from 'react';
import { animated, useSpring } from 'react-spring';
import { useInView } from 'react-intersection-observer';
import LightboxImage from './LightboxImage';

const ImageGallery = ({
  images,
  showPagination = false,
  showSearch = showPagination,
}) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [imagesPerPage, setImagesPerPage] = useState(9);
  const [searchTerm, setSearchTerm] = useState('');
  const closeButtonRef = useRef(null);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredImages = useMemo(() => {
    if (!showSearch || !normalizedSearchTerm) return images;

    const keywords = normalizedSearchTerm.split(/\s+/);
    return images.filter((image) => {
      const searchableText = [
        image.title,
        image.description,
        ...(image.tags || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return keywords.every((keyword) => searchableText.includes(keyword));
    });
  }, [images, normalizedSearchTerm, showSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredImages.length / imagesPerPage));
  const startIndex = showPagination ? (currentPage - 1) * imagesPerPage : 0;
  const endIndex = showPagination ? startIndex + imagesPerPage : filteredImages.length;
  const visibleImages = filteredImages.slice(startIndex, endIndex);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!selectedImage) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedImage(null);
        return;
      }

      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const currentIndex = visibleImages.findIndex((image) => image.id === selectedImage.id);
      if (currentIndex < 0 || visibleImages.length < 2) return;
      const offset = event.key === 'ArrowLeft' ? -1 : 1;
      const nextIndex = (currentIndex + offset + visibleImages.length) % visibleImages.length;
      setSelectedImage(visibleImages[nextIndex]);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage, visibleImages]);

  const popoverAnimation = useSpring({
    opacity: selectedImage ? 1 : 0,
    transform: selectedImage ? 'scale(1)' : 'scale(0.98)',
    config: { tension: 300, friction: 20 },
  });

  const changePage = (nextPage) => {
    setCurrentPage(nextPage);
    setSelectedImage(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const moveLightbox = (offset) => {
    const currentIndex = visibleImages.findIndex((image) => image.id === selectedImage.id);
    const nextIndex = (currentIndex + offset + visibleImages.length) % visibleImages.length;
    setSelectedImage(visibleImages[nextIndex]);
  };

  return (
    <div>
      {showSearch && (
        <div className="mb-10 grid gap-5 border border-[#d8d0be] bg-[#fbf8ef] p-5 sm:p-7 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
          <div>
            <label
              htmlFor="gallery-search"
              className="block text-xs font-semibold uppercase tracking-[0.24em] text-[#8e3a2b]"
            >
              Search the gallery
            </label>
            <div className="relative mt-3">
              <span
                className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-xl text-[#70806f]"
                aria-hidden="true"
              >
                &#9906;
              </span>
              <input
                id="gallery-search"
                type="search"
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setCurrentPage(1);
                  setSelectedImage(null);
                }}
                placeholder="Try flowers, harvest, compost, or pond"
                className="w-full border border-[#b9b29f] bg-white py-3.5 pl-12 pr-4 text-base text-[#18271e] outline-none transition focus:border-[#8e3a2b] focus:ring-2 focus:ring-[#8e3a2b]/25"
              />
            </div>
          </div>
          <p className="text-sm font-medium text-[#5f6c60]" aria-live="polite">
            {filteredImages.length} {filteredImages.length === 1 ? 'photo' : 'photos'}
          </p>
        </div>
      )}

      {visibleImages.length > 0 ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {visibleImages.map((image) => (
            <LazyImageCard key={image.id} image={image} onClick={setSelectedImage} />
          ))}
        </div>
      ) : (
        <div className="border border-dashed border-[#b9b29f] bg-[#fbf8ef] px-6 py-16 text-center">
          <p className="font-serif text-3xl text-[#10351f]">No photos found</p>
          <p className="mt-3 text-[#5f6c60]">Try a broader search, such as “flowers” or “garden.”</p>
          <button
            type="button"
            onClick={() => setSearchTerm('')}
            className="mt-6 border border-[#43513f] px-5 py-3 text-sm font-semibold text-[#10351f] transition-colors hover:bg-[#10351f] hover:text-white"
          >
            Clear search
          </button>
        </div>
      )}

      {showPagination && filteredImages.length > 0 && (
        <div className="mt-10 flex flex-col items-center justify-between gap-5 border-t border-[#cfc7b6] pt-7 sm:flex-row">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => changePage(currentPage - 1)}
              disabled={currentPage === 1}
              className="border border-[#43513f] px-4 py-2.5 text-sm font-semibold text-[#10351f] transition-colors hover:bg-[#10351f] hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
            >
              &larr; Previous
            </button>
            <span className="min-w-28 text-center text-sm font-medium text-[#5f6c60]">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => changePage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="border border-[#43513f] px-4 py-2.5 text-sm font-semibold text-[#10351f] transition-colors hover:bg-[#10351f] hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
            >
              Next &rarr;
            </button>
          </div>

          <label className="flex items-center gap-3 text-sm font-medium text-[#5f6c60]">
            Photos per page
            <select
              value={imagesPerPage}
              onChange={(event) => {
                setImagesPerPage(Number(event.target.value));
                setCurrentPage(1);
              }}
              className="border border-[#b9b29f] bg-white px-3 py-2 text-[#18271e] outline-none focus:border-[#8e3a2b] focus:ring-2 focus:ring-[#8e3a2b]/25"
            >
              <option value="6">6</option>
              <option value="9">9</option>
              <option value="12">12</option>
              <option value="18">18</option>
            </select>
          </label>
        </div>
      )}

      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 px-3 py-5 sm:px-8"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSelectedImage(null);
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Garden photo viewer"
        >
          <animated.div
            style={popoverAnimation}
            className="relative flex max-h-full w-full max-w-screen-xl flex-col items-center"
          >
            <button
              ref={closeButtonRef}
              type="button"
              onClick={() => setSelectedImage(null)}
              className="absolute right-2 top-2 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-black/65 text-3xl text-white transition hover:bg-white hover:text-black focus:outline-none focus:ring-2 focus:ring-white"
              aria-label="Close photo viewer"
            >
              &times;
            </button>

            <LightboxImage
              src={selectedImage.url}
              alt={selectedImage.description || selectedImage.title || 'Emma’s Court garden'}
            />

            {visibleImages.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => moveLightbox(-1)}
                  className="absolute left-2 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/65 text-3xl text-white transition hover:bg-white hover:text-black focus:outline-none focus:ring-2 focus:ring-white"
                  aria-label="Previous photo"
                >
                  &lsaquo;
                </button>
                <button
                  type="button"
                  onClick={() => moveLightbox(1)}
                  className="absolute right-2 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/65 text-3xl text-white transition hover:bg-white hover:text-black focus:outline-none focus:ring-2 focus:ring-white"
                  aria-label="Next photo"
                >
                  &rsaquo;
                </button>
              </>
            )}

            {selectedImage.description?.trim() && (
              <div className="w-full max-w-3xl bg-black/65 px-6 py-3 text-center text-white">
                <p className="text-base sm:text-lg">{selectedImage.description}</p>
              </div>
            )}
          </animated.div>
        </div>
      )}
    </div>
  );
};

const LazyImageCard = ({ image, onClick }) => {
  const { ref, inView } = useInView({ triggerOnce: true, rootMargin: '200px' });
  const [status, setStatus] = useState('loading');

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => onClick(image)}
      className="group relative block aspect-square w-full overflow-hidden border border-[#d3cbbb] bg-[#e5ddcd] text-left focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#f1eadc]"
      aria-label={`View photo: ${image.description || image.title || 'Garden photo'}`}
    >
      {status === 'loading' && (
        <span className="absolute inset-0 overflow-hidden" aria-hidden="true">
          <span className="absolute inset-y-0 -left-1/2 w-1/2 animate-[gallery-shimmer_1.4s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/55 to-transparent" />
        </span>
      )}

      {inView && (
        <img
          src={image.thumbnailUrl || image.url}
          alt={image.description || image.title || 'Emma’s Court garden'}
          loading="lazy"
          decoding="async"
          onLoad={() => setStatus('loaded')}
          onError={() => setStatus('error')}
          className={`h-full w-full object-cover transition duration-500 group-hover:scale-[1.025] ${
            status === 'loaded' ? 'opacity-100 blur-0' : 'opacity-0 blur-md'
          }`}
        />
      )}

      {status === 'error' && (
        <span className="absolute inset-0 flex items-center justify-center px-4 text-center text-sm font-semibold text-[#5f6c60]">
          Photo unavailable
        </span>
      )}

      <span className="absolute inset-x-0 bottom-0 translate-y-full bg-gradient-to-t from-black/80 to-transparent px-4 pb-4 pt-12 text-sm font-medium text-white transition-transform duration-300 group-hover:translate-y-0 group-focus:translate-y-0">
        {image.description || image.title || 'View garden photo'}
      </span>
    </button>
  );
};

export default ImageGallery;
